import { useState } from "react";
import { ArrowRight, Check, Globe } from "lucide-react";
import { Reveal, Chapter } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/i18n";

const PKG_TOPIC_IDX = { training: 1, nutrition: 2, bundle: 3 };

const SpecRow = ({ k, v }) => (
  <div className="flex justify-between gap-6 py-3 border-b border-white/10 text-sm">
    <span className="uppercase tracking-[0.15em] text-neutral-500 font-bold">{k}</span>
    <span className="text-white font-semibold text-right">{v}</span>
  </div>
);

const Coaching = () => {
  const { t } = useLang();
  const c = t.coaching;
  const [pkg, setPkg] = useState("bundle");
  const active = c.standard.packages.find((p) => p.id === pkg);

  const enquire = (idx) => {
    sessionStorage.setItem("fb-topic-idx", String(idx));
    window.dispatchEvent(new Event("fb-topic"));
    scrollToId("contact");
  };

  return (
    <section id="coaching" data-testid="coaching-section" className="py-24 md:py-32 bg-[#0A0A0A] border-y border-white/10">
      <div className="container-x">
        <Chapter num="02" label={c.chapter} />

        <Reveal>
          <h2 className="font-display text-4xl md:text-5xl lg:text-7xl uppercase tracking-tight leading-[0.9]">
            {c.titleA} <span className="text-[#D91624]">{c.titleB}</span>
          </h2>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-neutral-400 leading-relaxed">
            {c.intro}
          </p>
          <p className="mt-4 flex items-center gap-2 text-sm text-neutral-500">
            <Globe size={14} className="text-[#D4AF37]" />
            {c.langNote}
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal delay={0.1}>
            <div data-testid="coaching-standard-card" className="h-full bg-[#121212] border border-white/10 border-t-4 border-t-[#D91624] p-8 md:p-10 flex flex-col">
              <p className="overline-gold !text-neutral-500">{c.standard.tag}</p>
              <h3 className="font-display text-4xl md:text-5xl uppercase mt-3">{c.standard.name}</h3>
              <p className="mt-2 text-neutral-400">{c.standard.sub}</p>

              <div className="mt-6">
                {c.standard.specs.map(([k, v]) => (
                  <SpecRow key={k} k={k} v={v} />
                ))}
              </div>

              <p className="mt-8 text-xs uppercase tracking-[0.2em] text-neutral-500 font-bold">
                {c.standard.choose}
              </p>
              <div className="mt-3 flex flex-col gap-2">
                {c.standard.packages.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`standard-package-${p.id}`}
                    onClick={() => setPkg(p.id)}
                    className={`flex items-center justify-between border px-4 py-3 text-left transition-colors duration-300 cursor-pointer ${
                      pkg === p.id
                        ? "border-[#D91624] bg-[#D91624]/10"
                        : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <span className="text-sm font-semibold">{p.label}</span>
                    <span className="font-display text-xl text-[#D4AF37]">{p.price}</span>
                  </button>
                ))}
              </div>

              <ul className="mt-8 space-y-3">
                {c.standard.features.map((f) => (
                  <li key={f} className="flex gap-3 text-sm text-neutral-300">
                    <Check size={16} className="text-[#D91624] shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                data-testid="standard-get-program-btn"
                onClick={() => enquire(PKG_TOPIC_IDX[pkg])}
                className="btn-primary w-full mt-8"
              >
                {c.standard.cta} — {active.price} <ArrowRight size={16} />
              </button>
              <p className="mt-3 text-xs text-neutral-600 text-center">
                {c.standard.note}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div data-testid="coaching-elite-card" className="relative h-full bg-[#121212] border border-white/10 border-t-4 border-t-[#D4AF37] p-8 md:p-10 flex flex-col">
              <span className="absolute top-0 right-8 -translate-y-1/2 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-[0.2em] px-4 py-2">
                {c.elite.badge}
              </span>
              <p className="overline-gold !text-neutral-500">{c.elite.tag}</p>
              <h3 className="font-display text-4xl md:text-5xl uppercase mt-3 text-[#D4AF37]">{c.elite.name}</h3>
              <p className="mt-2 text-neutral-400">{c.elite.sub}</p>

              <div className="mt-6">
                {c.elite.specs.map(([k, v]) => (
                  <SpecRow key={k} k={k} v={v} />
                ))}
              </div>

              <div className="mt-8 border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-4 flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.2em] text-neutral-400 font-bold">
                  {c.elite.billing}
                </span>
                <span className="font-display text-2xl text-[#D4AF37]">
                  {c.elite.price} <span className="text-sm text-neutral-500">{c.elite.per}</span>
                </span>
              </div>

              <ul className="mt-8 space-y-3">
                {c.elite.features.map((f) => (
                  <li key={f} className="flex gap-3 text-sm text-neutral-300">
                    <Check size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                data-testid="elite-join-btn"
                onClick={() => enquire(4)}
                className="w-full mt-8 inline-flex items-center justify-center gap-3 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-[0.2em] text-xs md:text-sm px-8 py-4 transition-colors duration-300 cursor-pointer"
              >
                {c.elite.cta} <ArrowRight size={16} />
              </button>
              <p className="mt-3 text-xs text-neutral-600 text-center">
                {c.elite.note}
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Coaching;
